import React, { useCallback, useEffect, useMemo, useState } from 'react'
import OrdersConatiner from './conatiner'
import { useSelector } from 'react-redux'
import { useChangeOrderStatusMutation, useCollectOrderPaymentMutation, useCompleteOrderPaymentMutation, useGetOrdersQuery } from './services'
import { Box, Button, Chip, CircularProgress, MenuItem, Select, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import dayjs from 'dayjs'
import { currencySymbol, ordersFilterConfigs, statusConfigs, statusOptions } from 'utils/constant'
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog'
import { formatCurrency } from 'utils/function'
import ViewOrderDialog from 'components/Modal/ViewOrderDialog'
import { useLocation, useNavigate } from 'react-router-dom'
import CollectOrderPayment from 'components/Modal/CollectOrderPayment'
import OrderItemChefAssign from 'components/Modal/OrderItemChefAssign'
import OrderRiderAssign from 'components/Modal/OrderRiderAssign'
import { useSocket } from 'context/SocketContext'
import { useGetSettingsQuery } from '../settings/ManageSettings/services'

const Orders = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userData = useSelector((state) => state?.posData)
    const [paginationModel, setPaginationModel] = useState(() => ({
        page: window.history.state?.prevPage ?? 0,
        pageSize: 10
    }));
    const [filter, setFilter] = useState(() => window.history.state?.prevFilter ?? 'All');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [viewOrder, setViewOrder] = useState(null);

    const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
    const [paymentRow, setPaymentRow] = useState(null);
    const [paymentModeOrder, setPaymentModeOrder] = useState("cash");

    const [assignChefOpen, setAssignChefOpen] = useState(false);
    const [assignChefData, setAssignChefData] = useState(null);
    const [assignChefSelections, setAssignChefSelections] = useState({});
    const [pendingStatus, setPendingStatus] = useState(null);

    const [riderAssignOpen, setRiderAssignOpen] = useState(false);
    const [selectedRider, setSelectedRider] = useState(null);
    const [pendingRiderRow, setPendingRiderRow] = useState(null);

    const [collectPaymentApi, { isLoading: isCollecting }] = useCollectOrderPaymentMutation();
    const [completeOrderPaymentApi, { isLoading: isCompleting }] = useCompleteOrderPaymentMutation();

    // Clear nav state only when page is reloaded, so browser back/forward navigation keeps state
    // useEffect(() => {
    //     const isReload = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'reload' ||
    //         window.performance?.navigation?.type === 1;
    //     if (isReload) {
    //         window.history.replaceState({}, '');
    //     }
    // }, []);

    useEffect(() => {
        const handlePopState = (event) => {
            const historyState = event.state;

            if (historyState?.prevPage !== undefined) {
                setFilter(historyState.prevFilter);

                setPaginationModel((prev) => ({
                    ...prev,
                    page: historyState.prevPage
                }));
            }
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    // debounce searchTerm → debouncedSearch
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm]);
    const shouldSearch = debouncedSearch.length >= 3 ? debouncedSearch : '';

    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pendingRow, setPendingRow] = useState(null);
    const [updatingRowId, setUpdatingRowId] = useState(null);

    const { data: resp, isLoading, isError, isFetching, refetch } = useGetOrdersQuery({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        order_type: filter,
        search: shouldSearch
    }, { refetchOnMountOrArgChange: true });

    const [changeStatusApi, { isLoading: isUpdating }] = useChangeOrderStatusMutation();
    const { data: settingsResp } = useGetSettingsQuery();
    const socket = useSocket();

    useEffect(() => {
        if (!socket) return;

        const handleUpdate = (data) => {
            console.log("Socket order update received in Orders list:", data);
            refetch();
        };

        const handleNewQrOrder = (orderData) => {
            console.log("New QR order received via socket:", orderData);
            refetch();
        };

        socket.on('order_status_update', handleUpdate);
        socket.on('order_item_status_update', handleUpdate);
        socket.on('order_items_update', handleUpdate);
        socket.on('table_status_update', handleUpdate);
        socket.on('new_qr_order', handleNewQrOrder);

        return () => {
            socket.off('order_status_update', handleUpdate);
            socket.off('order_item_status_update', handleUpdate);
            socket.off('order_items_update', handleUpdate);
            socket.off('table_status_update', handleUpdate);
            socket.off('new_qr_order', handleNewQrOrder);
        };
    }, [socket, refetch, settingsResp, userData]);

    const orderTypeFlags = resp?.data?.state?.order_type_flags ?? resp?.data?.stats?.order_type_flags ?? resp?.data?.order_type_flags;

    const orderSummaryDetails = [
        {
            id: "1",
            title: "Total Orders",
            subtitle: "(Today)",
            value: resp?.data?.stats?.totalOrdersCount,
            highlight: true,
        },
        {
            id: "2",
            title: "Pending/Serving",
            value: resp?.data?.stats?.pendingServingCount,
            highlight: true,
        },
        {
            id: "3",
            title: "Completed Orders",
            value: resp?.data?.stats?.completedCount,
            highlight: true,
        },
        {
            id: "4",
            title: "Today’s Revenue",
            // value: `${formatCurrency(resp?.data?.stats?.todayRevenue, userData?.vendor?.currency == "EURO" ? "EUR" : "USD")}`,
            value: formatCurrency(
                resp?.data?.stats?.todayRevenue || 0,
                userData?.vendor?.currency
            ),
            highlight: true,
        },
    ];

    // pull out records / total count safely
    const records = resp?.data?.records || [];
    const totalCount = resp?.data?.totalCount || 0;

    const handleStatusChange = async (data, status, extraBody = {}) => {
        const id = data?.id;
        setUpdatingRowId(data?.id);
        const body = { order_status: status, ...extraBody };
        try {
            await changeStatusApi({ id, body });
        } catch (error) {
            console.log("Error in Changestatus API", error)
        } finally {
            setUpdatingRowId(null);
        }
    }

    const handleStatusWithChefCheck = (order, newStatus) => {
        const itemsWithoutChef = (order?.orderItems || []).filter(
            item =>
                item.stationData?.chefs?.length > 1 &&
                !item.primary_chef_id
        );
        if (itemsWithoutChef.length > 0 && (newStatus === "preparing" || newStatus === "ready_for_chef")) {
            // open assign chef modal
            setAssignChefData({
                orderId: order.id,
                items: itemsWithoutChef
            });

            // Default select first chef in dropdown
            const selections = {};
            itemsWithoutChef.forEach(item => {
                selections[item.menu_item_id] = item.stationData.chefs[0].id;
            });

            setAssignChefSelections(selections);
            setPendingStatus(newStatus);
            setAssignChefOpen(true);
        } else {
            // no chef selection needed, just update status
            handleStatusChange(order, newStatus);
        }
    };

    const handleStatusWithRiderCheck = async (order, newStatus) => {
        if (newStatus === "pickedup" && (order.order_type === "delivery" || order.order_type === "phone_order")) {
            if (!order.qr_code_token) {
                setUpdatingRowId(order.id);
                try {
                    // Send current status to force backend to attach a token if missing without advancing state early
                    const res = await changeStatusApi({ id: order.id, body: { order_status: order.order_status } }).unwrap();
                    setPendingRiderRow(res.data);
                    setRiderAssignOpen(true);
                    refetch();
                } catch (error) {
                    console.log("Error generating QR token:", error);
                } finally {
                    setUpdatingRowId(null);
                }
            } else {
                setPendingRiderRow(order);
                setRiderAssignOpen(true);
            }
        } else {
            handleStatusWithChefCheck(order, newStatus);
        }
    };

    const handleAssignChefChange = (itemId, chefId) => {
        setAssignChefSelections(prev => ({
            ...prev,
            [itemId]: chefId,
        }));
    };

    const handleAssignChefSubmit = async () => {
        if (!assignChefData) return;

        const payload = {
            order_id: assignChefData.orderId,
            order_status: pendingStatus,
            chef_assignments: Object.entries(assignChefSelections).map(([order_item_id, chef_id]) => ({
                order_item_id: Number(order_item_id),
                chef_id: Number(chef_id),
            })),
        };
        try {
            await changeStatusApi({ id: assignChefData.orderId, body: payload });
            setAssignChefOpen(false);
            setAssignChefData(null);
            setAssignChefSelections({});
            setPendingStatus(null);
            refetch();
        } catch (error) {
            console.error("Error submitting chef assignments:", error);
        }
    };


    const handleOpenDialog = (row, action) => {
        setPendingRow(row);
        setPendingAction(action);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setPendingRow(null);
        setPendingAction(null);
    };

    // const handleConfirm = async () => {
    //     if (pendingRow && pendingAction) {
    //         await handleStatusChange(pendingRow, pendingAction);
    //     }
    //     handleCloseDialog();
    // };

    const handleConfirm = async () => {
        if (pendingRow && pendingAction) {
            try {
                if (pendingAction === "completed") {
                    // ✅ Use completeOrderPayment API instead of changeStatus
                    const payload = {
                        id: pendingRow?.id,
                        paid_amount: pendingRow?.total_amount, // full amount (already paid)
                        payment_mode: pendingRow?.payment_mode || "cash", // fallback to cash
                    };

                    await completeOrderPaymentApi(payload);
                } else {
                    // Default: normal status update
                    await handleStatusChange(pendingRow, pendingAction);
                }
            } catch (error) {
                console.log("Error in handleConfirm", error);
            } finally {
                handleCloseDialog();
                // refetch();
            }
        }
    };


    const handleViewDialog = (row) => {
        setViewOrder(row)
        setOpenModal(true)
        // setWasViewOpenBeforeChefAssign(viewOrderDialogOpen);
    }

    const handleCloseViewDialog = () => {
        setOpenModal(false);
        refetch();
    }

    // 🔹 called when status "completed" selected
    const handleCompleteFlow = (row) => {
        const alreadyPaid = row?.paid_amount || 0;
        const totalAmount = row?.total_amount || 0;
        if (row?.payment_status !== "paid" || alreadyPaid < totalAmount) {
            // open payment modal
            setPaymentRow(row);
            setCollectPaymentOpen(true);
        } else {
            // open confirmation dialog directly
            handleOpenDialog(row, "completed");
        }
    };

    const handleCollectPayment = async (paymentMode) => {
        if (!paymentRow) return;
        const alreadyPaid = paymentRow?.paid_amount || 0;
        const totalAmount = paymentRow?.total_amount || 0;
        const remainingAmount = totalAmount - alreadyPaid;

        const payload = {
            id: paymentRow?.id,
            paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
            payment_mode: paymentMode
        };

        try {
            const res = await collectPaymentApi(payload);
            if (res?.data?.status == 200) {
                setCollectPaymentOpen(false);
                refetch();
            }
        } catch (error) {
            console.log("Error in Collect Payment", error);
        }
    };

    const handleCollectPaymentAndComplete = async (paymentMode) => {
        if (!paymentRow) return;
        const alreadyPaid = paymentRow?.paid_amount || 0;
        const totalAmount = paymentRow?.total_amount || 0;
        const remainingAmount = totalAmount - alreadyPaid;

        const payload = {
            id: paymentRow?.id,
            paid_amount: remainingAmount > 0 ? parseFloat(remainingAmount).toFixed(2) : parseFloat(totalAmount).toFixed(2),
            payment_mode: paymentMode
        };

        try {
            const res = await completeOrderPaymentApi(payload);
            if (res?.data?.status == 200) {
                setCollectPaymentOpen(false);
                refetch();
            }
        } catch (error) {
            console.log("Error in Collect Payment & Complete", error);
        }
    };

    const handleDetails = (row) => {
        window.history.replaceState(
            {
                ...window.history.state,
                prevFilter: filter,
                prevPage: paginationModel.page
            },
            ""
        );
        navigate("/order-details", { state: { orderId: row?.id, prevFilter: filter, prevPage: paginationModel.page } });
    }

    // const handleAssignChefSubmit = async (values) => {
    //     if (!pendingRow) return;

    //     const chefId = values.chefId;

    //     try {
    //         await handleStatusChange({ ...pendingRow, chefId }, pendingAction);
    //     } catch (error) {
    //         console.log("Error assigning chef:", error);
    //     } finally {
    //         setAssignChefOpen(false);
    //         setAssignChefData(null);
    //         setAssignChefOptions([]);
    //         setPendingRow(null);
    //         setPendingAction(null);
    //     }
    // };

    const columns = [
        {
            headerName: 'Order #',
            field: 'order_id',
            flex: 1,
            minWidth: 180,
            renderHeader: () => (
                <Box sx={{ pl: '15px', fontWeight: 600 }}>Order #</Box>
            ),
            // renderCell: ({ row }) => 
            // <Box className='d-flex align-items-center'>
            //     {!row?.is_view && <Box className='not-view-order-symbol'></Box>}
            //     <Box sx={{paddingLeft:'15px'}}>
            //         #{row.order_id}
            //     </Box>
            // </Box>,
            renderCell: ({ row }) => {
                const seen = row.is_view;    // or however you want to interpret it
                return (
                    <Box className='d-flex align-items-center'
                        sx={{
                            pl: '5px',
                            height: '100%',
                        }}
                    >
                        {/* always reserve an 8×8px dot */}
                        <Box className='not-view-order-symbol' sx={{ bgcolor: seen ? 'transparent' : '#1F9619' }} />
                        <Box sx={{ ml: '15px', fontWeight: 500 }}>#{row.order_id}</Box>
                    </Box>
                )
            }
        },
        {
            headerName: 'Ordered at',
            field: 'created_at',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => {
                // Parse the full ISO timestamp
                const created = dayjs(row.created_at);

                // If it's today, show "TODAY"; otherwise format as "DD MMM YYYY"
                const isToday = created.isSame(dayjs(), 'day');
                const displayDate = isToday
                    ? 'TODAY'
                    : created.format('DD MMM YYYY');

                // Format time as "hh:mm A"
                const displayTime = created.format('hh:mm A');

                return (
                    <Box sx={{ alignContent: 'space-evenly', height: '100%', paddingLeft: "10px" }}>
                        <Typography variant='body2'>{displayDate}</Typography>
                        <Typography variant="body2">
                            {displayTime}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            headerName: 'Order Type',
            field: 'order_type',
            flex: 1,
            minWidth: 140,
            renderCell: ({ row }) => {
                const cfg = ordersFilterConfigs.find((f) => f.id === row.order_type) || {
                    label: row.order_type,
                    color: '#000',
                    backgroundColor: '#e0e0e0',
                };
                const collectionName = row?.collectionPoint?.name
                return (
                    <Box sx={{ height: "100%", alignContent: "center", paddingLeft: "10px" }}>
                        <Typography variant='body2'
                            sx={{
                                color: cfg.color,
                                fontWeight: 500,
                                // height: '100%',
                                alignContent: 'space-evenly',
                            }}
                        >
                            {cfg.label}
                        </Typography>
                        {row.order_type == "collection_point" &&
                            <Typography color={cfg.color} fontSize="12px">({collectionName})</Typography>
                        }
                        {row.order_type == "dine_in" &&
                            <Typography fontSize="12px">({row?.table_name})</Typography>
                        }
                    </Box>
                );
            },
        },
        {
            headerName: 'Customer',
            field: 'customer',
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => {
                const info = row.customerVendor?.customerInfo || {};
                const name = row.customerVendor?.customer_full_name || row.customer_name;
                const phone = info.phone || row.customer_mobile;
                const countryCode = info.country_code || '';
                return (
                    <Box sx={{ alignContent: 'space-evenly', height: '100%', paddingLeft: "10px" }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>{name || '-'}</Typography>
                        {phone ? (
                            <Typography variant='body2'>
                                {countryCode} {phone}
                            </Typography>
                        ) : null}
                    </Box>
                );
            },
        },
        {
            headerName: 'Order Total',
            field: 'total_amount',
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => (
                <Box sx={{ alignContent: 'space-evenly', height: '100%', paddingLeft: "10px" }}>
                    <Typography variant='body2'>{currencySymbol(userData?.vendor?.currency)}
                        {(
                            parseFloat(row?.total_amount || 0) +
                            (row?.rider_tip_amount > 0 && row?.order_type === 'delivery'
                                ? parseFloat(row?.rider_tip_amount || 0)
                                : 0)
                        ).toFixed(2)}
                    </Typography>
                    <Typography variant='body2'>{`(${row?.total_item_count} Items)`}</Typography>
                </Box>
            ),
        },
        {
            headerName: 'Status',
            field: 'order_status',
            flex: 1,
            minWidth: 140,
            renderCell: ({ row }) => {
                const current = row.order_status;
                const flow = statusOptions[row.order_type] || [];
                let next = flow[current] ? [...flow[current]] : [];

                // If payment is already done for a delivery order, skip "delivered" layer 
                // and go straight to "completed" (labeled as "Delivered & Completed").
                const isPaid = row?.payment_status === "paid" ||
                    (row?.paid_amount && row?.total_amount && parseFloat(row?.paid_amount) >= parseFloat(row?.total_amount));

                if ((row.order_type === 'delivery' || row.order_type === 'phone_order') && isPaid) {
                    if (current === 'out_for_delivery') {
                        next = ['completed'];
                    } else if (current === 'pickedup') {
                        // In case the user actually meant to skip "out_for_delivery" as well, 
                        // though logically they just abbreviated it in their message. 
                        // We will just keep out_for_delivery here as per normal flow.
                    }
                }

                const isStatusLocked =
                    row?.order_status === "accept" && row?.total_item_count === 0;
                return (
                    <Select
                        value={current}
                        size="small"
                        disabled={isStatusLocked}
                        // IconComponent={ArrowDropDownIcon}
                        IconComponent={
                            updatingRowId == row.id ?
                                // ? () => 
                                () => <CircularProgress size={16} sx={{ color: '#000', position: 'absolute', right: '7px' }} />
                                : ArrowDropDownIcon
                        }
                        renderValue={() => {
                            if ((row.order_type === 'delivery' || row.order_type === 'phone_order') && isPaid && current === 'completed') {
                                return "Delivered & Completed";
                            }
                            return statusConfigs[current]?.label;
                        }}
                        // onChange={e => handleStatusChange(row, e.target.value)}
                        // onChange={(e) => {
                        //     const chosen = e.target.value;
                        //     if (['cancel', 'reject'].includes(chosen)) {
                        //         // only show confirmation for cancel/reject
                        //         handleOpenDialog(row, chosen);
                        //     } else if (chosen === "completed") {
                        //         // 🔹 handle complete with payment check
                        //         handleCompleteFlow(row);
                        //     } else {
                        //         // immediate change for other statuses
                        //         handleStatusChange(row, chosen);
                        //     }
                        // }}
                        onChange={(e) => {
                            const chosen = e.target.value;
                            if (['cancel', 'reject'].includes(chosen)) {
                                handleOpenDialog(row, chosen);
                            } else if (chosen === "completed") {
                                handleCompleteFlow(row);
                            } else {
                                handleStatusWithRiderCheck(row, chosen);
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: statusConfigs[current].bg,
                                color: '#000',
                                borderRadius: '8px',
                                '& fieldset': { border: 'none' },
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: "none !important",
                                borderColor: 'transparent !important'
                            },
                            '& .MuiSelect-select': {
                                py: '4px', px: '8px',
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: statusConfigs[current].bg,
                                color: '#000'
                            },
                            '& .MuiSelect-icon': {
                                color: '#000',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 'none !important',
                                borderColor: 'transparent !important'
                            },
                            // remove the default focus ring / box-shadow
                            '& .MuiOutlinedInput-root.Mui-focused': {
                                outline: 'none !important',
                                boxShadow: 'none !important',
                                borderColor: 'transparent !important',
                                border: 'none !important'
                            },
                            width: 150,
                            paddingLeft: "10px"
                        }}
                        MenuProps={{
                            PaperProps: { sx: { mt: 1, width: "170px" } },
                        }}
                    >
                        {next.map(statusKey => {
                            let optionLabel = statusConfigs[statusKey]?.optionLabel;
                            if ((row.order_type === 'delivery' || row.order_type === 'phone_order') && isPaid && statusKey === 'completed') {
                                optionLabel = "Delivered & Completed";
                            }
                            return (
                                <MenuItem
                                    key={statusKey}
                                    value={statusKey}
                                    sx={{ textTransform: 'none', fontSize: '14px' }}
                                >
                                    {optionLabel}
                                </MenuItem>
                            );
                        })}
                    </Select>
                );
            },
        },
        {
            headerName: '',
            field: 'actions',
            minWidth: 150,
            renderCell: ({ row }) => (
                <Box className='d-flex align-items-center' sx={{ gap: 2, height: '100%' }}>
                    <VisibilityIcon fontSize="small" sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewDialog(row)}
                    />
                    <Button size="small" variant='outlined' endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none' }}
                        onClick={() => handleDetails(row)}
                    >
                        Details
                    </Button>
                </Box>
            ),
        },
    ];

    const handleViewPastOrders = () => {
        navigate("/orders/past-orders")
    }

    return (
        <>
            <OrdersConatiner
                orderSummaryDetails={orderSummaryDetails}
                userData={userData}
                setFilter={(val) => { setFilter(val); setPaginationModel(prev => ({ ...prev, page: 0 })); }}
                filter={filter}
                columns={columns}
                rows={records}
                totalCount={totalCount}
                loading={isLoading}
                searchQuery={searchTerm}
                setSearchQuery={setSearchTerm}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                searchLoader={isFetching}
                handleViewPastOrders={handleViewPastOrders}
                orderTypeFlags={orderTypeFlags}
            />

            <ViewOrderDialog
                data={viewOrder}
                open={openModal}
                setOpenModal={setOpenModal}
                onClose={handleCloseViewDialog}
            />

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={handleCloseDialog}
                handleConfirm={handleConfirm}
                btnText={'Confirm'}
                infoText={`Are you sure you want to change status to ${pendingAction === 'completed' && (pendingRow?.order_type === 'delivery' || pendingRow?.order_type === 'phone_order') && (pendingRow?.payment_status === "paid" || (pendingRow?.paid_amount && pendingRow?.total_amount && parseFloat(pendingRow?.paid_amount) >= parseFloat(pendingRow?.total_amount)))
                    ? 'delivered & completed'
                    : statusConfigs[pendingAction]?.label.toLowerCase()
                    }?`}
                isLoading={isUpdating || isCompleting}
                action={
                    pendingAction === 'completed' && (pendingRow?.order_type === 'delivery' || pendingRow?.order_type === 'phone_order') && (pendingRow?.payment_status === "paid" || (pendingRow?.paid_amount && pendingRow?.total_amount && parseFloat(pendingRow?.paid_amount) >= parseFloat(pendingRow?.total_amount)))
                        ? 'Delivered & Completed'
                        : statusConfigs[pendingAction]?.label || ''
                }
            />

            {/* ✅ Collect Payment Modal */}
            <CollectOrderPayment
                open={collectPaymentOpen}
                onClose={() => setCollectPaymentOpen(false)}
                orderDetails={paymentRow}
                onCollectPayment={handleCollectPayment}
                onCollectAndComplete={handleCollectPaymentAndComplete}
                isCollecting={isCollecting}
                isCompleting={isCompleting}
            />

            {/* <ConformChefModal
            open={assignChefOpen}
            onClose={() => setAssignChefOpen(false)}
            initialData={assignChefData}
            chefOptions={assignChefOptions}
            onSubmit={handleAssignChefSubmit}
            loading={isUpdating}
        /> */}

            <OrderItemChefAssign
                open={assignChefOpen}
                onClose={() => setAssignChefOpen(false)}
                orderItems={assignChefData?.items || []}
                selections={assignChefSelections}
                onChangeChef={handleAssignChefChange}
                onSubmit={handleAssignChefSubmit}
                loading={isUpdating}
            />

            <OrderRiderAssign
                open={riderAssignOpen}
                onClose={() => {
                    setRiderAssignOpen(false);
                    setPendingRiderRow(null);
                    setSelectedRider(null);
                }}
                onSubmit={async () => {
                    if (!pendingRiderRow || !selectedRider) return;
                    try {
                        await handleStatusChange(pendingRiderRow, "pickedup", { rider_id: selectedRider });
                        setRiderAssignOpen(false);
                        setPendingRiderRow(null);
                        setSelectedRider(null);
                        refetch();
                    } catch (error) {
                        console.error("Error in rider assign submit", error);
                    }
                }}
                loading={isUpdating}
                selectedRider={selectedRider}
                setSelectedRider={setSelectedRider}
                orderDetails={pendingRiderRow}
            />

        </>
    )
}

export default Orders
